import type { Ruleset } from "./types.js";

/**
 * MIS 3005-D:2025 §3.4 — heat pump sizing and selection rules.
 */
export interface SizingRules {
  /**
   * The heat pump must provide AT LEAST this fraction of the calculated heat load
   * at design conditions, with no contribution from supplementary electric heaters
   * (§3.4.1(e), §3.4.3(d)).
   *
   * Note the standard sets a floor, not a ceiling: there is no numeric oversize cap.
   * Oversizing is discouraged through the MCS 031 performance-estimate warnings.
   */
  readonly minCapacityFraction: number;
  /**
   * Hybrid systems: the heat pump alone must provide at least this fraction of the
   * calculated heat load, rated at hybridRatingFlowTempC (§3.4.2).
   */
  readonly hybridMinHeatPumpFraction: number;
  readonly hybridRatingFlowTempC: number;
  /**
   * Designs proposing a flow temperature above this must also offer an alternative
   * design at or below it, with the efficiency difference explained (§3.4.4, §3.4.5).
   */
  readonly alternativeDesignFlowTempThresholdC: number;
  /**
   * Air-to-air: every habitable room must have an indoor unit, and each indoor unit
   * must cover at least this fraction of its own room's load (§3.4.3(g),(h)).
   */
  readonly airToAirPerRoomFraction: number;
}

export const MIS_3005_D_SIZING: Ruleset<SizingRules> = {
  id: "mcs.sizing",
  version: "1.0.0",
  effectiveFrom: "2025-01-01",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "verified",
  source: {
    standard: "MIS 3005-D:2025",
    issue: "2.0",
    clause: "3.4",
    issued: "2025-12-05",
    url: "https://mcscertified.com/wp-content/uploads/2025/12/MIS-3005-D-2025-V2.0-Final.pdf",
  },
  data: {
    minCapacityFraction: 1.0,
    hybridMinHeatPumpFraction: 0.55,
    hybridRatingFlowTempC: 55,
    alternativeDesignFlowTempThresholdC: 55,
    airToAirPerRoomFraction: 1.0,
  },
};
